import React, { useEffect, useMemo, useState } from 'react';
import { otherNewsAPI } from '../utils/api/otherNewsAPI';
import ShareButtons from './ShareButtons';
import { hasTrackedView, markTrackedView, formatEngagementCount } from '../utils/contentEngagement';
import '../styles/CategoryPage.css';

const FALLBACK_IMAGE = 'https://images.loksattaimg.com/2026/03/New-Project-2026-03-13T162455.659.jpg?w=440';

function formatDate(value) {
  if (!value) {
    return 'अभी';
  }

  return new Date(value).toLocaleDateString('hi-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

function excerpt(text, limit = 180) {
  if (!text) {
    return '';
  }

  return text.length > limit ? `${text.slice(0, limit)}...` : text;
}

function MenuNewsPage({ menuTitle, submenu, pageTitle, subtitle, emoji }) {
  const [articles, setArticles] = useState([]);
  const [expandedIds, setExpandedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const trackArticleView = async (id) => {
    if (!id || hasTrackedView('other-news', id)) {
      return;
    }

    try {
      const response = await otherNewsAPI.incrementView(id);

      if (response.success && response.data?.views !== undefined) {
        setArticles((current) => current.map((article) => (
          article._id === id
            ? {
                ...article,
                views: response.data.views,
                shareCount: response.data.shareCount ?? article.shareCount
              }
            : article
        )));
      }

      markTrackedView('other-news', id);
    } catch (error) {
      console.error('Failed to increment other news view count:', error);
    }
  };

  useEffect(() => {
    let ignore = false;

    async function loadNews() {
      try {
        setLoading(true);
        setError('');
        const response = await otherNewsAPI.getNews({ menuTitle, submenu, limit: 100 });

        if (!ignore) {
          if (response.success) {
            setArticles(response.data || []);
          } else {
            setError(response.message || 'डेटा लोड नहीं हो सका');
          }
        }
      } catch (loadError) {
        if (!ignore) {
          setError('डेटा लोड नहीं हो सका');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    loadNews();
    return () => {
      ignore = true;
    };
  }, [menuTitle, submenu]);

  useEffect(() => {
    if (!articles.length || typeof window === 'undefined') {
      return;
    }

    const matchedId = window.location.hash.startsWith('#article=')
      ? window.location.hash.replace('#article=', '')
      : '';

    if (!matchedId || !articles.some((article) => article._id === matchedId)) {
      return;
    }

    setExpandedIds((current) => current.includes(matchedId) ? current : [...current, matchedId]);
    trackArticleView(matchedId);
  }, [articles]);

  const recentNews = useMemo(() => articles.slice(0, 5), [articles]);
  const latestArticle = recentNews[0] || null;

  const toggleExpanded = async (id) => {
    const shouldExpand = !expandedIds.includes(id);

    if (shouldExpand) {
      await trackArticleView(id);

      if (typeof window !== 'undefined') {
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#article=${id}`);
      }
    } else if (typeof window !== 'undefined' && window.location.hash === `#article=${id}`) {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    }

    setExpandedIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );
  };

  return (
    <div className="category-page">
      <div className="page-header">
        <h1>{emoji} {pageTitle}</h1>
        <p>{subtitle}</p>
      </div>

      <div className="page-container menu-news-layout">
        <div className="main-content">
          {loading && (
            <div className="sidebar-box">
              <h3>लोड हो रहा है...</h3>
              <p>समाचार लाए जा रहे हैं।</p>
            </div>
          )}

          {!loading && error && (
            <div className="sidebar-box">
              <h3>त्रुटि</h3>
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && articles.length === 0 && (
            <div className="sidebar-box">
              <h3>कोई समाचार नहीं मिला</h3>
              <p>{menuTitle} / {submenu} के लिए अभी कोई प्रकाशित समाचार उपलब्ध नहीं है।</p>
            </div>
          )}

          {!loading && !error && articles.map((article, index) => {
            const isExpanded = expandedIds.includes(article._id);
            const shownContent = isExpanded ? article.content : excerpt(article.content, index === 0 ? 320 : 180);

            return (
              <article key={article._id} className={`news-article ${index === 0 ? 'featured' : ''}`}>
                <img
                  src={article.image || FALLBACK_IMAGE}
                  alt={article.title}
                  className="article-image"
                />
                <div className="article-content">
                  <div>
                    <span className="category-tag">{menuTitle} | {submenu}</span>
                    <h2>{article.title}</h2>
                    <p>{article.description}</p>
                    <p style={{ whiteSpace: 'pre-line' }}>{shownContent}</p>
                    {!!article.tags?.length && (
                      <p>
                        <strong>टैग:</strong> {article.tags.join(', ')}
                      </p>
                    )}
                    {(article.location || article.author) && (
                      <p>
                        {article.location ? <><strong>स्थान:</strong> {article.location}</> : null}
                        {article.location && article.author ? ' | ' : ''}
                        {article.author ? <><strong>लेखक:</strong> {article.author}</> : null}
                      </p>
                    )}
                    <p>
                      <strong>Views:</strong> {formatEngagementCount(article.views)}
                      {' | '}
                      <strong>Shares:</strong> {formatEngagementCount(article.shareCount)}
                    </p>
                    {article.videoUrl && (
                      <p>
                        <strong>वीडियो:</strong>{' '}
                        <a href={article.videoUrl} target="_blank" rel="noreferrer">
                          लिंक खोलें
                        </a>
                      </p>
                    )}
                    {isExpanded ? (
                      <ShareButtons
                        title={article.title}
                        url={typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}#article=${article._id}` : `/#article=${article._id}`}
                        description={article.description}
                        contentId={article._id}
                        contentType="other-news"
                        compact
                      />
                    ) : null}
                  </div>
                  <div className="article-footer">
                    <span className="time">{formatDate(article.createdAt)}</span>
                    <button className="read-btn" onClick={() => toggleExpanded(article._id)}>
                      {isExpanded ? 'कम करें' : 'पूरा पढ़ें'}
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <aside className="sidebar menu-news-sidebar">
          <div className="sidebar-box menu-overview-box">
            <h3>सेक्शन ओवरव्यू</h3>
            <div className="menu-summary-grid">
              <div className="summary-tile">
                <span className="summary-label">कैटेगरी</span>
                <strong>{menuTitle}</strong>
              </div>
              <div className="summary-tile">
                <span className="summary-label">सबमेनू</span>
                <strong>{submenu}</strong>
              </div>
              <div className="summary-tile">
                <span className="summary-label">कुल खबरें</span>
                <strong>{articles.length}</strong>
              </div>
              <div className="summary-tile">
                <span className="summary-label">आखिरी अपडेट</span>
                <strong>{latestArticle ? formatDate(latestArticle.createdAt) : 'अभी नहीं'}</strong>
              </div>
            </div>
          </div>

          <div className="sidebar-box">
            <h3>हाल की खबरें</h3>
            {recentNews.length === 0 ? (
              <p>अभी कोई हाल की खबर उपलब्ध नहीं है।</p>
            ) : (
              recentNews.map((article, index) => (
                <div key={article._id} className="trending-item">
                  <span className="trending-num">{index + 1}</span>
                  <div>
                    <p>{article.title}</p>
                    <small>{formatDate(article.createdAt)}</small>
                  </div>
                </div>
              ))
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

export default MenuNewsPage;
